import { useQuery } from "@apollo/client";
import { Dialog, Transition } from "@headlessui/react";
import {
  ArrowsUpDownIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/solid";
import { ColumnDef, getCoreRowModel } from "@tanstack/react-table";
import { Fragment, useMemo, useState } from "react";
import { DebounceInput } from "react-debounce-input";

import { Spinner } from "../../animations";
import {
  GET_WAREHOUSE_PICKERS,
  Picker,
} from "../../graphql/user-management/assign-zone-pickers";
import { renderStatus } from "../../graphql/user-management/users";
import { Role } from "../../modules/auth/@types/auth";
import { classNames } from "../../utils";
import { ErrorFallback } from "../core";
import {
  IndeterminateCheckbox,
  TableCursor,
  TableStaticInfinite,
} from "../table";
import { Button } from ".";

interface RowUser extends Picker {
  index: number;
}

export function FieldTableWarehousePickers({
  title,
  data: previews,
  value = [],
  onChange,
  excludeIds = [],
  excludeNote = "",
}: {
  title: string;
  data: Picker[];
  value: number[];
  onChange: (newValue: Picker[]) => void;
  excludeIds?: number[];
  excludeNote?: string;
}) {
  const [query, setQuery] = useState<string>("");
  const [pageLimit] = useState(10);
  const [searchQuery, setSearchQuery] = useState<string | null>(null);

  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});

  const { data, loading, error } = useQuery(GET_WAREHOUSE_PICKERS);

  const users: RowUser[] = useMemo(() => {
    const updatedWarehousePickers: RowUser[] =
      data?.fetchWarehousePickers?.flatMap((user: Picker, index: number) => ({
        ...user,
        index,
      })) ?? [];

    if (value.length > 0) {
      const updatedRowSelection: Record<string, boolean> = {};
      updatedWarehousePickers.forEach((p, i) => {
        updatedRowSelection[String(i)] = value.includes(Number(p.id));
      });
      setRowSelection(updatedRowSelection);
    } else {
      setRowSelection({});
    }

    return updatedWarehousePickers;
  }, [data?.fetchWarehousePickers, value]);

  const columns = useMemo<ColumnDef<RowUser, any>[]>(
    () => [
      {
        id: "select",
        header: "Select",
        cell: ({ row }) => {
          return (
            <div className="px-1">
              <IndeterminateCheckbox
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect() || row.depth > 0,
                  indeterminate: row.getIsSomeSelected(),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    const { checked } = e.target;
                    row.getToggleSelectedHandler()(e);
                    if (checked) {
                      onChange([...previews, row.original]);
                      return;
                    }
                    onChange(previews.filter((p) => p.id !== row.original.id));
                  },
                }}
              />
            </div>
          );
        },
        size: 20,
        enableHiding: false,
      },
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "fullName",
        header: "Name",
        size: 300,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.fullName,
      },
      {
        accessorKey: "email",
        header: "Email",
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue()}</span>
        ),
        accessorFn: (row) => row.email,
      },
      {
        accessorKey: "roles",
        header: "Role",
        cell: (props: any) =>
          props
            .getValue()
            .map((r: Role) => r.roleName)
            .join(", "),
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (props) => renderStatus(props.getValue()),
      },
    ],
    [onChange, previews]
  );

  const previewColumns = useMemo<ColumnDef<Picker>[]>(
    () => [
      {
        id: "index",
        header: "No.",
        size: 30,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.row.index + 1}.</span>
        ),
      },
      {
        accessorKey: "id",
        header: "ID",
        size: 30,
        enableHiding: false,
      },
      {
        accessorKey: "fullName",
        header: "Name",
        size: 300,
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue<string>()}</span>
        ),
        accessorFn: (row) => row.fullName,
      },
      {
        accessorKey: "email",
        header: "Email",
        enableHiding: false,
        cell: (props) => (
          <span className="whitespace-nowrap">{props.getValue<string>()}</span>
        ),
        accessorFn: (row) => row.email,
      },
      {
        id: "remove",
        header: "Remove",
        cell: (props) => (
          <div>
            <Button
              variant="text"
              onClick={() => {
                const newWarehousePickers = previews.filter(
                  (p) => p.id !== props.row.original.id
                );
                onChange(newWarehousePickers);
              }}
            >
              <span className="sr-only">
                Remove {props.row.original.fullName}
              </span>
              <span
                aria-hidden="true"
                className="bi bi-trash3 h-8 w-8 text-lg text-gray-500"
              />
            </Button>
          </div>
        ),
        enableHiding: false,
        enableSorting: false,
        enableGlobalFilter: false,
      },
    ],
    [onChange, previews]
  );

  const filteredSelectedWarehousePickers = useMemo(
    () =>
      query === ""
        ? previews
        : previews.filter((p) =>
            p.fullName?.toLowerCase().includes(query?.toLowerCase())
          ),
    [previews, query]
  );

  if (error) return <ErrorFallback error={error} />;

  return (
    <>
      <label className="mb-1 block text-sm font-medium text-gray-900">
        {title}
      </label>
      <header className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="relative col-span-3">
          <label htmlFor="search" className="sr-only">
            Search warehouse pickers
          </label>
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-3">
            <MagnifyingGlassIcon
              className="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
          <input
            type="search"
            name="search"
            id="search"
            className={classNames(
              "relative block w-full appearance-none rounded-md border border-gray-300 py-2.5 pl-10 pr-3 focus:outline-none focus-visible:border-primary-500 focus-visible:ring-4 focus-visible:ring-primary-50 sm:text-sm",
              "disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500",
              "read-only:cursor-not-allowed read-only:border-primary-200 read-only:bg-primary-50 read-only:text-primary-500"
            )}
            placeholder="Search warehouse pickers"
            value={query || ""}
            onChange={(e) => {
              previews.length > 0
                ? setQuery(e.target.value)
                : setSearchQuery(e.target.value);
            }}
          />
        </div>
        <div className="col-span-1 flex">
          <Button
            variant="secondary"
            className="w-full justify-center"
            onClick={() => {
              setSearchQuery("");
            }}
          >
            Browse
          </Button>
        </div>
      </header>
      <div className="mt-2">
        {loading ? (
          <div className="flex justify-center py-6 md:py-12">
            <Spinner className="h-8 w-8 text-primary-700" />
          </div>
        ) : previews.length > 0 ? (
          <div className="rounded-lg bg-greyish pt-2">
            <TableStaticInfinite
              data={filteredSelectedWarehousePickers}
              columns={previewColumns}
              loading={loading}
              filtering={query.length > 0}
              totalRows={previews.length}
              pageLimit={pageLimit}
              getCoreRowModel={getCoreRowModel()}
              className=""
            />
          </div>
        ) : (
          <div className="py-6 text-center text-sm md:py-12">
            <ExclamationCircleIcon
              type="outline"
              name="exclamation-circle"
              className="mx-auto h-6 w-6 text-gray-400"
            />
            <p className="mt-4 font-medium text-gray-900">
              No warehouse pickers selected
            </p>
            <p className="mt-2 text-gray-500">
              Please{" "}
              <Button
                variant="text"
                onClick={() => {
                  setSearchQuery("");
                }}
              >
                browse
              </Button>{" "}
              warehouse pickers to add to the zone.
            </p>
          </div>
        )}
      </div>
      <Transition.Root
        show={searchQuery === null ? false : true}
        as={Fragment}
        appear
      >
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => {
            setSearchQuery(null);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-25 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-4xl transform divide-y divide-gray-100 overflow-hidden rounded-2xl bg-greyish p-4 shadow-2xl ring-1 ring-black ring-opacity-5 transition-all">
                <div className="mb-2 flex flex-wrap items-start space-x-2 xl:flex-nowrap">
                  <div className="flex items-center rounded-lg border border-gray-200 bg-white pl-3 text-black/50 xl:flex-1">
                    <MagnifyingGlassIcon className="h-4 w-4 min-w-[1rem] text-black/70" />
                    <span className="ml-3 block h-4 w-[1px] bg-gray-400"></span>
                    <DebounceInput
                      type="search"
                      className={classNames(
                        "ml-1 h-11 w-full border-none bg-transparent p-2 text-sm font-normal text-black",
                        "shadow-none outline-none focus:ring-0",
                        "placeholder-black/50 placeholder-opacity-100"
                      )}
                      placeholder="Search for name"
                      minLength={2}
                      debounceTimeout={300}
                      value={searchQuery ?? ""}
                      onChange={(e: {
                        target: { value: React.SetStateAction<string | null> };
                      }) => {
                        setSearchQuery(e.target.value);
                      }}
                    />
                  </div>
                  <Button
                    onClick={() => {
                      setSearchQuery(null);
                    }}
                  >
                    <ArrowsUpDownIcon className="mr-2 h-5 w-5" />
                    Done
                  </Button>
                </div>
                <TableCursor
                  data={users}
                  columns={columns}
                  loading={loading}
                  totalRows={users.length || 0}
                  state={{
                    rowSelection,
                  }}
                  enableRowSelection={(row) =>
                    !excludeIds.includes(Number(row.original.id))
                  }
                  enableSubRowSelection={false}
                  onRowSelectionChange={setRowSelection}
                  getCoreRowModel={getCoreRowModel()}
                />
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
}
